import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import { Loader2, MapPin, CheckCircle, AlertTriangle, MousePointerClick, Info } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getLocationSession, updateLocationSession } from '../../services/apiService';
import { getMapTileAttribution, getMapTileUrl } from '../../config/mapTiles';

// Fix Leaflet icons
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl,
    iconUrl,
    shadowUrl,
});

const LocationPicker = ({ setManualLocation, manualMode }) => {
    useMapEvents({
        click(e) {
            if (manualMode) {
                setManualLocation([e.latlng.lat, e.latlng.lng]);
            }
        },
    });
    return null;
};

const RecenterMap = ({ coords }) => {
    const map = useMap();
    useEffect(() => {
        if (coords) {
            map.invalidateSize();
            map.flyTo(coords, 16, { animate: true, duration: 1.5 });
        }
    }, [coords, map]);
    return null;
};

const ClientLocationPage = () => {
    const { sessionId } = useParams();
    const [status, setStatus] = useState('loading'); // loading, active, expired, completed, error
    const [location, setLocation] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [message, setMessage] = useState('');
    const [manualMode, setManualMode] = useState(false);
    const [manualLocation, setManualLocation] = useState(null);

    useEffect(() => {
        const checkSession = async () => {
            try {
                const data = await getLocationSession(sessionId);
                if (data.status === 'expired') {
                    setStatus('expired');
                } else {
                    setStatus('active');
                    if (data.location) {
                        setLocation([data.location.lat, data.location.lng]);
                        setStatus('completed');
                    }
                }
            } catch (error) {
                console.error("Erro ao verificar sessão:", error);
                setStatus('error');
            }
        };

        if (sessionId) {
            checkSession();
        }
    }, [sessionId]);

    const handleGetLocation = () => {
        if (!navigator.geolocation) {
            setMessage('Geolocalização não é suportada pelo seu navegador.');
            return;
        }

        setIsUpdating(true);
        setMessage('Obtendo sua localização...');

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;
                setLocation([lat, lng]);

                try {
                    await updateLocationSession(sessionId, lat, lng);
                    setStatus('completed');
                    setMessage('Localização enviada com sucesso! Obrigado.');
                } catch (error) {
                    console.error("Erro ao atualizar localização:", error);
                    setMessage('Erro ao enviar localização. Tente novamente.');
                } finally {
                    setIsUpdating(false);
                }
            },
            (error) => {
                console.error("Erro de geolocalização:", error);
                setIsUpdating(false);
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        setMessage('Permissão negada. Desbloqueie no seu navegador ou clique no mapa para selecionar o local manualmente.');
                        setManualMode(true);
                        break;
                    case error.POSITION_UNAVAILABLE:
                        setMessage('Localização indisponível. Clique no mapa para selecionar o local manualmente.');
                        setManualMode(true);
                        break;
                    case error.TIMEOUT:
                        setMessage('Tempo expirado. Clique no mapa para selecionar o local manualmente.');
                        setManualMode(true);
                        break;
                    default:
                        setMessage('Ocorreu um erro. Clique no mapa para selecionar o local manualmente.');
                        setManualMode(true);
                        break;
                }
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
    };

    const handleManualSubmit = async () => {
        if (!manualLocation) return;

        setIsUpdating(true);
        setMessage('Enviando localização selecionada...');

        try {
            await updateLocationSession(sessionId, manualLocation[0], manualLocation[1]);
            setLocation(manualLocation);
            setStatus('completed');
            setMessage('Localização enviada com sucesso! Obrigado.');
        } catch (error) {
            console.error("Erro ao atualizar localização manualmente:", error);
            setMessage('Erro ao enviar localização. Tente novamente.');
        } finally {
            setIsUpdating(false);
        }
    };

    if (status === 'loading') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
            </div>
        );
    }

    if (status === 'expired') {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-4 text-center">
                <AlertTriangle className="w-16 h-16 text-amber-500 mb-4" />
                <h1 className="text-2xl font-bold text-slate-800 mb-2">Link Expirado</h1>
                <p className="text-slate-600">Este link de compartilhamento de localização não é mais válido.</p>
                <p className="text-slate-600 mt-2">Por favor, solicite um novo link.</p>
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-4 text-center">
                <AlertTriangle className="w-16 h-16 text-red-500 mb-4" />
                <h1 className="text-2xl font-bold text-slate-800 mb-2">Erro</h1>
                <p className="text-slate-600">Não foi possível carregar as informações da sessão.</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-100 flex flex-col">
            <header className="bg-white shadow-sm p-4 text-center z-10">
                <h1 className="text-lg font-bold text-slate-800">Compartilhar Localização</h1>
                <p className="text-xs text-slate-500">Connect10 Internet</p>
            </header>

            <div className="flex-1 flex flex-col p-4 max-w-md mx-auto w-full">
                <div className="bg-white rounded-xl shadow-lg overflow-hidden flex-1 flex flex-col relative z-0">
                    <div className="flex-1 relative min-h-[400px]">
                        <MapContainer
                            center={location || [-14.2350, -51.9253]}
                            zoom={location ? 16 : 4}
                            style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0 }}
                            zoomControl={false}
                        >
                            <TileLayer
                                url={getMapTileUrl()}
                                attribution={getMapTileAttribution()}
                            />
                            <LocationPicker setManualLocation={setManualLocation} manualMode={manualMode} />
                            {location && !manualMode && (
                                <>
                                    <RecenterMap coords={location} />
                                    <Marker position={location}>
                                        <Popup>Sua localização confirmada</Popup>
                                    </Marker>
                                </>
                            )}
                            {manualLocation && manualMode && status !== 'completed' && (
                                <>
                                    <RecenterMap coords={manualLocation} />
                                    <Marker position={manualLocation}>
                                        <Popup>Local selecionado manualmente</Popup>
                                    </Marker>
                                </>
                            )}
                        </MapContainer>

                        {/* Instructional Overlay for Manual Mode */}
                        {manualMode && status !== 'completed' && (
                            <div className="absolute top-4 left-4 right-4 bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded-lg shadow-sm z-[1000] flex items-start text-sm">
                                <Info className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 text-amber-500" />
                                <div>
                                    <p className="font-semibold mb-1">Busca automática falhou ou foi bloqueada.</p>
                                    <p>Por favor, <strong>navegue pelo mapa e toque no local exato</strong> de sua residência, depois clique no botão abaixo para enviar.</p>
                                </div>
                            </div>
                        )}

                        {/* Status Overlay */}
                        <div className="absolute bottom-0 left-0 right-0 p-6 bg-white/95 backdrop-blur-sm border-t border-slate-200 z-[1000] rounded-t-2xl shadow-lg transition-transform duration-300">
                            {status === 'completed' ? (
                                <div className="text-center">
                                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-2">
                                        <CheckCircle className="w-6 h-6 text-green-600" />
                                    </div>
                                    <h3 className="font-bold text-slate-800">Obrigado!</h3>
                                    <p className="text-sm text-slate-600">Sua localização foi enviada com sucesso.</p>
                                    <p className="text-xs text-slate-400 mt-2">Você pode fechar esta página.</p>
                                </div>
                            ) : (
                                <div className="text-center">
                                    {!manualMode ? (
                                        <>
                                            <p className="text-sm text-slate-600 mb-4">
                                                Precisamos confirmar sua localização exata para verificar a viabilidade técnica.
                                            </p>
                                            <button
                                                onClick={handleGetLocation}
                                                disabled={isUpdating}
                                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center transition-colors disabled:opacity-70 shadow-md"
                                            >
                                                {isUpdating ? (
                                                    <>
                                                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                                        Obtendo localização...
                                                    </>
                                                ) : (
                                                    <>
                                                        <MapPin className="w-5 h-5 mr-2" />
                                                        Confirmar minha localização automaticamente
                                                    </>
                                                )}
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <p className="text-sm text-slate-600 mb-4">
                                                {manualLocation
                                                    ? "Local selecionado! Confirme e envie."
                                                    : "Toque no mapa para marcar seu local exato."}
                                            </p>
                                            <button
                                                onClick={handleManualSubmit}
                                                disabled={isUpdating || !manualLocation}
                                                className={`w-full text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center transition-colors shadow-md ${
                                                    manualLocation && !isUpdating
                                                        ? 'bg-amber-600 hover:bg-amber-700'
                                                        : 'bg-slate-300 cursor-not-allowed text-slate-500'
                                                }`}
                                            >
                                                {isUpdating ? (
                                                    <>
                                                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                                        Enviando...
                                                    </>
                                                ) : (
                                                    <>
                                                        <MousePointerClick className="w-5 h-5 mr-2" />
                                                        Enviar Localização Selecionada
                                                    </>
                                                )}
                                            </button>
                                        </>
                                    )}
                                    {message && (
                                        <p className={`mt-3 text-sm font-medium ${message.includes('sucesso') ? 'text-green-600' : message.includes('Permissão') || message.includes('indisponível') || message.includes('expirado') || message.includes('erro') ? 'text-amber-600' : 'text-slate-500'}`}>
                                            {message}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientLocationPage;
